import { FileContainer } from './FileContainer';
import type { FileContainerAssetCopy } from './FileContainer';
import { newFileContainerId } from './FileContainerId';
import { Timestamp } from '@framework/Timestamp';
import type { ICommand } from '@model-framework/command';
import { CompositeCommand } from '@model-framework/command';
import type { FileContainerJSON, FileContainerId, ModelId, ViewModelAssetId, ViewModelId } from 'balus-common';

export type FileContainerCollectionJSON = FileContainerJSON[];

export class FileContainerCollection {
  private readonly ids: Readonly<FileContainerId[]>;
  private readonly containers: Readonly<Record<FileContainerId, FileContainer>>;

  constructor(containers: FileContainer[]) {
    this.containers = containers.reduce(
      (result, container) => {
        result[container.id] = container;
        return result;
      },
      {} as Record<FileContainerId, FileContainer>
    );

    this.ids = Object.values(this.containers)
      .sort((a, b) => a.createdAt.compareTo(b.createdAt))
      .map(({ id }) => id);
  }

  static buildEmpty(): FileContainerCollection {
    return new FileContainerCollection([]);
  }

  static load(dump: FileContainerCollectionJSON): FileContainerCollection {
    return new FileContainerCollection(
      dump.flatMap((json) => {
        try {
          return [FileContainer.load(json)];
        } catch (e) {
          console.error('FileContainer.load() failed');
          return [];
        }
      })
    );
  }

  dump(): FileContainerCollectionJSON {
    return this.ids.map((id) => this.containers[id].dump());
  }

  isEqual(other: FileContainerCollection): boolean {
    return (
      this.count() === other.count() && this.all().every((container) => other.get(container.id)?.isEqual(container))
    );
  }

  toRecord(): Record<FileContainerId, FileContainer> {
    return { ...this.containers };
  }

  cloneNew(assetCopy?: FileContainerAssetCopy): [FileContainerCollection, Record<FileContainerId, FileContainerId>] {
    const newIdMap = Object.fromEntries(this.allIds().map((id) => [id, newFileContainerId()]));
    const createdAt = Timestamp.now();
    const containers = this.all().map((container, index) =>
      container.cloneNew(newIdMap[container.id], createdAt.addMilliSeconds(index), assetCopy)
    );
    return [new FileContainerCollection(containers), newIdMap];
  }

  assetIds(): ViewModelAssetId[] {
    return this.all().map(({ assetId }) => assetId);
  }

  count(): number {
    return this.ids.length;
  }

  all(): FileContainer[] {
    return this.ids.map((id) => this.containers[id]);
  }

  allIds(): FileContainerId[] {
    return this.ids.concat();
  }

  filter(callback: (container: FileContainer, index: number) => boolean): FileContainerCollection {
    return new FileContainerCollection(this.all().filter(callback));
  }

  get(id: FileContainerId): FileContainer | undefined {
    return this.containers[id];
  }

  add(container: FileContainer): FileContainerCollection {
    return new FileContainerCollection(this.all().concat(container));
  }

  remove(id: FileContainerId): FileContainerCollection {
    return this.filter((container) => container.id !== id);
  }

  totalFileSize(): number {
    return this.all().reduce((total, container) => total + container.fileSize, 0);
  }

  buildCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    return new CompositeCommand(...this.all().map((container) => container.buildCreateCommand(viewModelId, modelId)));
  }
}
